import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { gestureHandlingText } from '../utils/leafletGestureHandling';

// Fix for default marker icons in Leaflet with Webpack/CRA
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Marker.prototype.options.icon = DefaultIcon;

// Helper component to fix map sizing issues in tabs/modals
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    // Small delay to ensure container is fully transitioned and rendered
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
};

interface Orchard {
  _id: string;
  orchardName: string;
  farmName: string;
  location?: {
    type: 'Point';
    coordinates: number[]; // [long, lat]
  };
  boundary?: {
    type: 'Polygon';
    coordinates: number[][][]; // [[ [long, lat], ... ]]
  };
  cultivar?: string;
  variety?: string;
}

interface OrchardMapProps {
  orchards: Orchard[];
  onOrchardClick?: (orchard: Orchard) => void;
}

const OrchardMap: React.FC<OrchardMapProps> = ({ orchards, onOrchardClick }) => {
  const { t } = useTranslation();
  // Default center if no orchards (using a generic coordinate)
  const defaultCenter: [number, number] = [-33.93, 18.84];
  
  // Calculate center based on orchards if available
  const center: [number, number] = orchards.length > 0 && orchards[0].location
    ? [orchards[0].location.coordinates[1], orchards[0].location.coordinates[0]]
    : defaultCenter;

  return (
    <div style={{ height: '500px', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
      <MapContainer center={center} zoom={13} gestureHandling={true} gestureHandlingOptions={gestureHandlingText(t)} style={{ height: '100%', width: '100%' }}>
        <MapResizer />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {orchards.map(o => (
          <React.Fragment key={o._id}>
            {/* Render Point Marker */}
            {o.location && (
              <Marker position={[o.location.coordinates[1], o.location.coordinates[0]]}>
                <Popup>
                  <div>
                    <h6>{o.orchardName}</h6>
                    <p className="mb-1 text-muted">{o.farmName}</p>
                    <p className="mb-2">{o.cultivar} {o.variety}</p>
                    {onOrchardClick && (
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => onOrchardClick(o)}
                      >
                        View Details
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Render Polygon Boundary */}
            {o.boundary && o.boundary.coordinates[0] && (
              <Polygon 
                positions={o.boundary.coordinates[0].map(coord => [coord[1], coord[0]] as [number, number])}
                pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.2 }}
              >
                <Popup>
                  <div>
                    <h6>{o.orchardName}</h6>
                    <p className="mb-0 text-muted">{o.farmName}</p>
                  </div>
                </Popup>
              </Polygon>
            )}
          </React.Fragment>
        ))}
      </MapContainer>
    </div>
  );
};

export default OrchardMap;
